import { inject, Injectable } from '@angular/core';

import { ConverterService } from '../converter/converter.service';
import { ArrayBufferBuilder } from './buffer.builder';
import { ArrayBufferDestructor } from './buffer.destructor';

@Injectable()
export class CryptoProvider {
  private readonly crypto = window.crypto;
  private readonly converterService = inject(ConverterService);

  private static readonly ALGORITHM = 'AES-CBC'; // bytes
  private static readonly IV_SIZE = 16; // bytes
  private static readonly SALT_SIZE = 16; // bytes
  private static readonly HASH_LENGTH_32_BYTE = 32 * 8;
  private static readonly PAYLOAD_TOO_SHORT = 'Encrypted payload is too short to contain salt, IV and ciphertext.';

  public async decrypt(
    passphrase: string,
    saltIvCipher: Blob | string,
    uint: Uint8ArrayConstructor,
  ): Promise<Uint8Array<ArrayBuffer>>;

  public async decrypt(
    passphrase: string,
    saltIvCipher: Blob | string,
    uint?: Uint16ArrayConstructor,
  ): Promise<Uint16Array<ArrayBuffer>>;

  public async decrypt(
    passphrase: string,
    saltIvCipher: Blob | string,
    uint: Uint8ArrayConstructor | Uint16ArrayConstructor = Uint16Array,
  ): Promise<Uint8Array<ArrayBuffer> | Uint16Array<ArrayBuffer>> {
    const { salt, iv, data } = await this.destructSaltIvCipher(saltIvCipher);

    const [key] = await this.getKey(passphrase, salt);
    const ab = await this.crypto.subtle.decrypt(
      {
        name: CryptoProvider.ALGORITHM,
        iv: iv,
      },
      key,
      data,
    );
    return new uint(ab);
  }
  public async computeHash(value: string, salt: Uint8Array<ArrayBuffer>): Promise<Uint8Array> {
    const enc = new TextEncoder();
    const key = await this.crypto.subtle.importKey('raw', enc.encode(value), 'PBKDF2', false, ['deriveBits']);
    const buffer = await this.crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 600000,
        hash: 'SHA-256',
      },
      key,
      CryptoProvider.HASH_LENGTH_32_BYTE,
    );

    return new Uint8Array(buffer);
  }

  public async encryptToBase64(
    passphrase: string,
    dataToEncrypt: Uint8Array<ArrayBuffer> | Uint16Array<ArrayBuffer>,
  ): Promise<string> {
    const encryptionResult = await this.encrypt(passphrase, dataToEncrypt);
    const combinedBuffer = new ArrayBufferBuilder()
      .add(encryptionResult.salt)
      .add(encryptionResult.iv)
      .add(encryptionResult.encryptedData)
      .build();

    const result = this.converterService.uIntArrayToBase64(combinedBuffer);
    if (result === null) {
      throw new Error('Encryption failed: base64 conversion returned null');
    }

    return result;
  }

  public async encryptToUint8Array(
    passphrase: string,
    dataToEncrypt: Uint8Array<ArrayBuffer> | Uint16Array<ArrayBuffer>,
  ): Promise<Uint8Array<ArrayBuffer>> {
    const encryptionResult = await this.encrypt(passphrase, dataToEncrypt);
    return new ArrayBufferBuilder()
      .add(encryptionResult.salt)
      .add(encryptionResult.iv)
      .add(encryptionResult.encryptedData)
      .build();
  }

  public async encryptToHex(
    passphrase: string,
    plaintext: Uint8Array<ArrayBuffer> | string | Uint16Array<ArrayBuffer>,
  ): Promise<string> {
    let dataToEncrypt: Uint8Array<ArrayBuffer> | Uint16Array<ArrayBuffer>;

    if (typeof plaintext === 'string') {
      dataToEncrypt = this.converterService.stringToUint16Array(plaintext);
    } else {
      dataToEncrypt = plaintext;
    }

    const encryptionResult = await this.encrypt(passphrase, dataToEncrypt);

    const ciphertext = CryptoProvider.unit8ArrayToHexString(new Uint8Array(encryptionResult.encryptedData));
    return `${CryptoProvider.unit8ArrayToHexString(encryptionResult.salt)}${CryptoProvider.unit8ArrayToHexString(encryptionResult.iv)}${ciphertext}`;
  }

  public async decryptToHexString(passphrase: string, saltIvCipher: string): Promise<string> {
    const decrypted = await this.decrypt(passphrase, saltIvCipher, Uint16Array);
    return this.converterService.uIntArrayToString(decrypted);
  }

  public async decryptFromBase64(passphrase: string, cipherText: string): Promise<ArrayBuffer> {
    const buffer = this.converterService.base64ToUintArray(cipherText).buffer;

    const [salt, iv, cipher] = ArrayBufferDestructor.destructArrayBuffer(buffer, [
      CryptoProvider.SALT_SIZE,
      CryptoProvider.IV_SIZE,
    ]);

    const [key] = await this.getKey(passphrase, new Uint8Array(salt));
    return await this.crypto.subtle.decrypt(
      {
        name: CryptoProvider.ALGORITHM,
        iv,
      },
      key,
      cipher,
    );
  }

  private async encrypt(
    passphrase: string,
    dataToEncrypt: Uint8Array<ArrayBuffer> | Uint16Array<ArrayBuffer>,
  ): Promise<{
    salt: Uint8Array<ArrayBuffer>;
    encryptedData: ArrayBuffer;
    iv: Uint8Array<ArrayBuffer>;
  }> {
    const ivBuf = this.crypto.getRandomValues(new Uint8Array(CryptoProvider.IV_SIZE));
    const [key, saltBuf] = await this.getKey(passphrase);
    const result = await this.crypto.subtle.encrypt(
      {
        name: CryptoProvider.ALGORITHM,
        iv: ivBuf,
      },
      key,
      dataToEncrypt,
    );

    return {
      salt: saltBuf,
      iv: ivBuf,
      encryptedData: result,
    };
  }

  /**
   * Splits an encrypted payload into its salt, IV and ciphertext parts.
   *
   * A Blob is read as bytes and sliced with `subarray`, i.e. without copying. Routing it through a
   * hex string instead — as this did previously — allocates two single-character strings per byte
   * plus the joined result, which measured at roughly 23 MB of JS heap per MiB of payload and ran
   * as one uninterruptible block. On mobile that exhausted the renderer's memory and terminated the
   * app while decrypting larger Bescheide, and it capped any payload at 64 MiB (V8's maximum array
   * length). Keep this path free of intermediate strings.
   *
   * Both forms reject a payload that is too short to hold all three parts. Without that check the
   * three slices come back empty and `crypto.subtle.decrypt` fails on a zero-length IV with a bare
   * `OperationError`, which callers map onto "decryption failed" and which therefore hides the
   * actual cause — a truncated payload.
   *
   * @param {Blob | string} saltIvCipher - Encrypted payload, either as raw bytes or as a hex string.
   * @return {Promise<{salt: Uint8Array, iv: Uint8Array, data: Uint8Array}>} The three parts, in order.
   */
  private async destructSaltIvCipher(saltIvCipher: Blob | string): Promise<{
    salt: Uint8Array<ArrayBuffer>;
    iv: Uint8Array<ArrayBuffer>;
    data: Uint8Array<ArrayBuffer>;
  }> {
    const ivEnd = CryptoProvider.SALT_SIZE + CryptoProvider.IV_SIZE;

    if (typeof saltIvCipher === 'string') {
      const saltHexEnd = CryptoProvider.SALT_SIZE * 2;
      const ivHexEnd = ivEnd * 2;
      if (saltIvCipher.length <= ivHexEnd) {
        throw new Error(CryptoProvider.PAYLOAD_TOO_SHORT);
      }

      return {
        salt: this.converterService.hexStringToUint8Array(saltIvCipher.substring(0, saltHexEnd)),
        iv: this.converterService.hexStringToUint8Array(saltIvCipher.substring(saltHexEnd, ivHexEnd)),
        data: this.converterService.hexStringToUint8Array(saltIvCipher.substring(ivHexEnd)),
      };
    }

    const bytes = new Uint8Array(await saltIvCipher.arrayBuffer());
    if (bytes.length <= ivEnd) {
      throw new Error(CryptoProvider.PAYLOAD_TOO_SHORT);
    }

    return {
      salt: bytes.subarray(0, CryptoProvider.SALT_SIZE),
      iv: bytes.subarray(CryptoProvider.SALT_SIZE, ivEnd),
      data: bytes.subarray(ivEnd),
    };
  }

  private async getKey(
    passphrase: string,
    salt?: Uint8Array<ArrayBuffer>,
  ): Promise<[CryptoKey, Uint8Array<ArrayBuffer>]> {
    salt ??= this.crypto.getRandomValues(new Uint8Array(CryptoProvider.SALT_SIZE));

    const enc = new TextEncoder();
    const keyMaterial = await this.crypto.subtle.importKey('raw', enc.encode(passphrase), 'PBKDF2', false, [
      'deriveBits',
    ]);
    const derivedBits = await this.crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 12000,
        hash: 'SHA-256',
      },
      keyMaterial,
      128,
    );
    const key = await this.crypto.subtle.importKey('raw', derivedBits, CryptoProvider.ALGORITHM, true, [
      'encrypt',
      'decrypt',
    ]);
    return [key, salt];
  }
  private static unit8ArrayToHexString(buf: Uint8Array): string {
    const hexChars = [];
    for (const bite of buf) {
      hexChars.push((bite >>> 4).toString(16));
      hexChars.push((bite & 0x0f).toString(16));
    }

    return hexChars.join('');
  }
}
